/**
 * Block variant resolution — naming for developers
 *
 * Logical identity: `{Block}-{Arrangement}-{Chrome}` (+ site Outfit).
 *
 * | Layer        | Meaning                         | Code location                                      |
 * |--------------|---------------------------------|----------------------------------------------------|
 * | Block        | Type entitlement (Hero, Nav…)   | `REGISTRY_BLOCK.type.*` / `componentType`          |
 * | Arrangement  | Structure / cut within the type | `registryBlockKey` → this file’s override map      |
 * | Chrome       | Finish / look (nav only today)  | `content.navPresentation` → `data-nav-presentation`|
 * | Outfit       | Site tokens                     | Theme / project CSS vars — not a TSX variant       |
 *
 * TSX rule:
 * - Hero: **one file per Arrangement**, name = PascalCase of registry key
 *   (`Hero_Editorial_Split` → `HeroEditorialSplit.tsx`). No Chrome suffix.
 * - Navigation: **one shell** (`NavigationHeader.tsx`); Arrangement + Chrome
 *   are data attrs, not separate files.
 */
import type { ComponentType } from 'react';
import {
  LAYOUT_COMPONENT_TYPE,
  REGISTRY_BLOCK,
  type LayoutComponentType,
  type RegistryBlockKey,
} from '@web-studio/shared-schemas';
import type { BlockRenderProps } from '../types';

import { HeroEditorialSplit } from './hero/HeroEditorialSplit';
import { HeroVideoAtmosphere } from './hero/HeroVideoAtmosphere';
import { HeroFullBleedImage } from './hero/HeroFullBleedImage';
import { HeroBrothSteam } from './hero/HeroBrothSteam';
import { HeroStandard } from './hero/HeroStandard';
import { HeroCoffeeSpotlight } from './hero/HeroCoffeeSpotlight';
import { HeroUtilityReservation } from './hero/HeroUtilityReservation';
import { HeroQuietLuxury } from './hero/HeroQuietLuxury';
import { HeroCounterBleed } from './hero/HeroCounterBleed';
import { HeroCollageStory } from './hero/HeroCollageStory';
import { HeroReelFade } from './hero/HeroReelFade';
import { NavigationHeader } from './navigation/NavigationHeader';
import { MenuGrid3Column } from './menu/staticmenu/MenuGrid3Column';
import { MenuPdfCard } from './menu/staticmenu/MenuPdfCard';
import { MenuCategoryGallery } from './menu/staticmenu/MenuCategoryGallery';
import { MenuDishCards } from './menu/staticmenu/MenuDishCards';
import { MenuEditorialBoard } from './menu/staticmenu/MenuEditorialBoard';
import { MenuStreetBoard } from './menu/staticmenu/MenuStreetBoard';
import { MenuHighlightStrip } from './menu/staticmenu/MenuHighlightStrip';
import { MenuHighlightMarquee } from './menu/highlight/MenuHighlightMarquee';
import { MenuHighlightHeroPlate } from './menu/highlight/MenuHighlightHeroPlate';
import { MenuHighlightZine } from './menu/highlight/MenuHighlightZine';
import { MenuHighlightChalkboard } from './menu/highlight/MenuHighlightChalkboard';
import { MenuHighlightChefsCut } from './menu/highlight/MenuHighlightChefsCut';
import { MenuHighlightTodaySplit } from './menu/highlight/MenuHighlightTodaySplit';
import { MenuHighlightPriceTiles } from './menu/highlight/MenuHighlightPriceTiles';
import { MenuHighlightDishCards } from './menu/highlight/MenuHighlightDishCards';
import { DynamicMenuFlow } from './menu/dynamicmenu/DynamicMenuFlow';
import { DrinksListStatic } from './menu/drinkslist/DrinksListStatic';
import { DrinksListCellar } from './menu/drinkslist/DrinksListCellar';
import { DrinksListSheet } from './menu/drinkslist/DrinksListSheet';
import { SimpleMenuStrip } from './menu/simplestrip/SimpleMenuStrip';
import { SimpleMenuBoard } from './menu/simplestrip/SimpleMenuBoard';
import { FooterStandard } from './footer/FooterStandard';
import { FooterColophon } from './footer/FooterColophon';
import { FooterDirectory } from './footer/FooterDirectory';
import { FooterUtility } from './footer/FooterUtility';
import { ChefStoryBlock } from './story/ChefStoryBlock';
import { KitchenTeamBlock } from './story/KitchenTeamBlock';
import { ImageGallery } from './gallery/ImageGallery';
import { TastingMenuCourseTimeline } from './story/TastingMenuCourseTimeline';
import { TastingMenuPairingCards } from './story/TastingMenuPairingCards';
import {
  ReservationCallBook,
  ReservationFormEmbed,
  ReservationMessengerBook,
  ReservationThirdPartyBook,
} from './reservation';
import { AllergyCaptureSecureForm } from './forms/AllergyCaptureSecureForm';
import { LegalImprintBlock } from './legal/imprint/LegalImprintBlock';
import { LegalPrivacyBlock } from './legal/privacy/LegalPrivacyBlock';
import { SimpleOpeningHours } from './hours/SimpleOpeningHours';
import { AddressMapBlock } from './location/AddressMapBlock';
import { ContactBlock } from './contact/ContactBlock';
import { ContactDesk } from './contact/ContactDesk';
import { ContactLedger } from './contact/ContactLedger';
import { ContactThreshold } from './contact/ContactThreshold';
import { StaticLocationText } from './location/StaticLocationText';
import { StaticLocationStop } from './location/StaticLocationStop';
import {
  LiveBakeryCounterList,
  SocialFeedGrid,
  CoffeeSubscriptionMatrix,
  PreOrderPickupForm,
} from './cafe';
import {
  DynamicCocktailSpotlight,
  PrivatePartyInquiryForm,
  PrivatePartyInquirySalon,
  LiveTapListSync,
  VipTableBookingStrip,
} from './bar';
import {
  WeeklyScheduleRouteStrip,
  WeeklyScheduleRouteBoard,
  CateringInquiryForm,
  LiveGpsLocationEmbed,
  PopUpTicketPurchaseCard,
} from './truck';
import { FaqBlock } from './faq';
import {
  AnnouncementBar,
  BackToTopFloat,
  ContactChannelsFloat,
  UtilityBar,
} from './chrome';

type BlockComponent = ComponentType<BlockRenderProps>;

/** Canonical component for each componentType. */
const COMPONENT_TYPE_MAP: Record<LayoutComponentType, BlockComponent> = {
  [LAYOUT_COMPONENT_TYPE.navigationHeader]: NavigationHeader,
  [LAYOUT_COMPONENT_TYPE.heroEditorialSplit]: HeroEditorialSplit,
  [LAYOUT_COMPONENT_TYPE.menuGrid3Column]: MenuGrid3Column,
  [LAYOUT_COMPONENT_TYPE.dynamicMenuFlow]: DynamicMenuFlow,
  [LAYOUT_COMPONENT_TYPE.reservationFormEmbed]: ReservationFormEmbed,
  [LAYOUT_COMPONENT_TYPE.reservationThirdPartyBook]: ReservationThirdPartyBook,
  [LAYOUT_COMPONENT_TYPE.reservationMessengerBook]: ReservationMessengerBook,
  [LAYOUT_COMPONENT_TYPE.reservationCallBook]: ReservationCallBook,
  [LAYOUT_COMPONENT_TYPE.chefStoryBlock]: ChefStoryBlock,
  [LAYOUT_COMPONENT_TYPE.kitchenTeamBlock]: KitchenTeamBlock,
  [LAYOUT_COMPONENT_TYPE.imageGallery]: ImageGallery,
  [LAYOUT_COMPONENT_TYPE.tastingMenuCourseTimeline]: TastingMenuCourseTimeline,
  [LAYOUT_COMPONENT_TYPE.allergyCaptureSecureForm]: AllergyCaptureSecureForm,
  [LAYOUT_COMPONENT_TYPE.heroVideoAtmosphere]: HeroVideoAtmosphere,
  [LAYOUT_COMPONENT_TYPE.footerStandard]: FooterStandard,
  [LAYOUT_COMPONENT_TYPE.legalImprintBlock]: LegalImprintBlock,
  [LAYOUT_COMPONENT_TYPE.legalPrivacyBlock]: LegalPrivacyBlock,
  [LAYOUT_COMPONENT_TYPE.simpleOpeningHours]: SimpleOpeningHours,
  [LAYOUT_COMPONENT_TYPE.addressMapBlock]: AddressMapBlock,
  [LAYOUT_COMPONENT_TYPE.drinksListStatic]: DrinksListStatic,
  [LAYOUT_COMPONENT_TYPE.staticLocationText]: StaticLocationText,
  [LAYOUT_COMPONENT_TYPE.simpleMenuStrip]: SimpleMenuStrip,
  [LAYOUT_COMPONENT_TYPE.liveBakeryCounterList]: LiveBakeryCounterList,
  [LAYOUT_COMPONENT_TYPE.socialFeedGrid]: SocialFeedGrid,
  [LAYOUT_COMPONENT_TYPE.coffeeSubscriptionMatrix]: CoffeeSubscriptionMatrix,
  [LAYOUT_COMPONENT_TYPE.preOrderPickupForm]: PreOrderPickupForm,
  [LAYOUT_COMPONENT_TYPE.dynamicCocktailSpotlight]: DynamicCocktailSpotlight,
  [LAYOUT_COMPONENT_TYPE.privatePartyInquiryForm]: PrivatePartyInquiryForm,
  [LAYOUT_COMPONENT_TYPE.liveTapListSync]: LiveTapListSync,
  [LAYOUT_COMPONENT_TYPE.vipTableBookingStrip]: VipTableBookingStrip,
  [LAYOUT_COMPONENT_TYPE.weeklyScheduleRouteStrip]: WeeklyScheduleRouteStrip,
  [LAYOUT_COMPONENT_TYPE.cateringInquiryForm]: CateringInquiryForm,
  [LAYOUT_COMPONENT_TYPE.liveGpsLocationEmbed]: LiveGpsLocationEmbed,
  [LAYOUT_COMPONENT_TYPE.popUpTicketPurchaseCard]: PopUpTicketPurchaseCard,
  [LAYOUT_COMPONENT_TYPE.contactBlock]: ContactBlock,
  [LAYOUT_COMPONENT_TYPE.faqBlock]: FaqBlock,
  [LAYOUT_COMPONENT_TYPE.backToTopFloat]: BackToTopFloat,
  [LAYOUT_COMPONENT_TYPE.contactChannelsFloat]: ContactChannelsFloat,
  [LAYOUT_COMPONENT_TYPE.utilityBar]: UtilityBar,
  [LAYOUT_COMPONENT_TYPE.announcementBar]: AnnouncementBar,
};

const P = REGISTRY_BLOCK.presentation;

/** Arrangement overrides — registry key → TSX (Hero only; name matches key). */
const REGISTRY_KEY_OVERRIDES: Partial<Record<RegistryBlockKey, BlockComponent>> = {
  [P.heroFullBleedImage]: HeroFullBleedImage,
  [P.heroBrothSteam]: HeroBrothSteam,
  [P.heroStandard]: HeroStandard,
  [P.heroCoffeeSpotlight]: HeroCoffeeSpotlight,
  [P.heroUtilityReservation]: HeroUtilityReservation,
  [P.heroQuietLuxury]: HeroQuietLuxury,
  [P.heroCounterBleed]: HeroCounterBleed,
  [P.heroCollageStory]: HeroCollageStory,
  [P.heroReelFade]: HeroReelFade,
  [P.staticMenuPdfCard]: MenuPdfCard,
  [P.staticMenuCategoryGallery]: MenuCategoryGallery,
  [P.staticMenuDishCards]: MenuDishCards,
  [P.staticMenuEditorialBoard]: MenuEditorialBoard,
  [P.staticMenuStreetBoard]: MenuStreetBoard,
  [P.staticMenuCategoryChips]: DynamicMenuFlow,
  [P.staticMenuHighlightStrip]: MenuHighlightStrip,
  [P.staticMenuHighlightMarquee]: MenuHighlightMarquee,
  [P.staticMenuHighlightHeroPlate]: MenuHighlightHeroPlate,
  [P.staticMenuHighlightZine]: MenuHighlightZine,
  [P.staticMenuHighlightChalkboard]: MenuHighlightChalkboard,
  [P.staticMenuHighlightChefsCut]: MenuHighlightChefsCut,
  [P.staticMenuHighlightTodaySplit]: MenuHighlightTodaySplit,
  [P.staticMenuHighlightPriceTiles]: MenuHighlightPriceTiles,
  [P.staticMenuHighlightDishCards]: MenuHighlightDishCards,
  [P.footerColophon]: FooterColophon,
  [P.footerDirectory]: FooterDirectory,
  [P.footerUtility]: FooterUtility,
  [P.contactDesk]: ContactDesk,
  [P.contactLedger]: ContactLedger,
  [P.contactThreshold]: ContactThreshold,
  [P.faqAccordion]: FaqBlock,
  [P.faqLedger]: FaqBlock,
  [P.backToTopCorner]: BackToTopFloat,
  [P.backToTopPill]: BackToTopFloat,
  [P.contactChannelsRail]: ContactChannelsFloat,
  [P.contactChannelsFab]: ContactChannelsFloat,
  [P.utilityBarStatic]: UtilityBar,
  [P.utilityBarSticky]: UtilityBar,
  [P.utilityBarCarousel]: UtilityBar,
  [P.utilityBarCollapse]: UtilityBar,
  [P.announcementBarBand]: AnnouncementBar,
  [P.drinksListCellar]: DrinksListCellar,
  [P.privatePartyInquirySalon]: PrivatePartyInquirySalon,
  [P.tastingMenuPairingCards]: TastingMenuPairingCards,
  [P.simpleMenuBoard]: SimpleMenuBoard,
  [P.staticLocationStop]: StaticLocationStop,
  [P.weeklyScheduleRouteBoard]: WeeklyScheduleRouteBoard,
};

/**
 * Resolves the React component for a block, preferring a registry-key-specific
 * Arrangement when one exists, otherwise falling back to the canonical component
 * for the componentType.
 */
export function resolveBlockVariant(
  componentType: LayoutComponentType,
  registryBlockKey?: RegistryBlockKey,
): BlockComponent | undefined {
  if (registryBlockKey && REGISTRY_KEY_OVERRIDES[registryBlockKey]) {
    return REGISTRY_KEY_OVERRIDES[registryBlockKey];
  }
  return COMPONENT_TYPE_MAP[componentType];
}
